// Minimal mana-cost renderer. Parses Scryfall's `{1}{U}{U}` / `{W/U}` /
// `{2/R}` / `{X}` mana-cost strings into small colored pips.
//
// Design philosophy: we're not mtg.wiki. A pip is a 14px circle with the
// single character inside. Hybrids get a half-and-half gradient. Generic
// mana uses a neutral gray. Good enough to read at a glance on a dense
// deck row without importing an icon font or SVG set.

import { cn } from "../lib/cn";

interface Props {
  cost: string | null | undefined;
  size?: "sm" | "md";
  className?: string;
}

// Palette matches the ManaSigil in MetaSnapshot.tsx (same set of colors
// used for the color-identity dots) for visual consistency.
const COLOR_CLASS: Record<string, string> = {
  W: "bg-[#f7f6d6] border-[#dad7a6] text-[#73623f]",
  U: "bg-[#b3cfe6] border-[#7fa7cd] text-[#2d4966]",
  B: "bg-[#7c7976] border-[#4a4946] text-[#1b1a17]",
  R: "bg-[#e09a8e] border-[#bd685a] text-[#5a1c12]",
  G: "bg-[#a1c2a1] border-[#6a9a6a] text-[#1e3b1e]",
  C: "bg-bg-raised border-border text-fg-muted",
  X: "bg-bg-raised border-border text-fg-muted",
};

function pipGradient(a: string, b: string): string | undefined {
  // Tailwind-safe inline style for hybrid pips. Using static hex so we
  // don't rely on dynamic class names.
  const swatches: Record<string, string> = {
    W: "#f7f6d6", U: "#b3cfe6", B: "#7c7976",
    R: "#e09a8e", G: "#a1c2a1", C: "#1d2229",
  };
  const sA = swatches[a];
  const sB = swatches[b];
  if (!sA || !sB) return undefined;
  return `linear-gradient(135deg, ${sA} 0%, ${sA} 48%, ${sB} 52%, ${sB} 100%)`;
}

export default function ManaCost({ cost, size = "sm", className }: Props) {
  if (!cost) return null;
  const pips = [...cost.matchAll(/\{([^}]+)\}/g)].map((m) => m[1]!);
  if (pips.length === 0) return null;

  const diameter = size === "sm" ? "h-[14px] w-[14px] text-[9px]" : "h-4 w-4 text-[10px]";

  return (
    <span className={cn("inline-flex items-center gap-[2px] align-middle", className)}>
      {pips.map((p, i) => {
        // Hybrid like "W/U" or "2/R" — split on slash.
        const parts = p.split("/");
        if (parts.length === 2) {
          const [a, b] = parts as [string, string];
          const grad = pipGradient(a, b);
          return (
            <span
              key={i}
              style={grad ? { background: grad } : undefined}
              className={cn(
                "inline-flex items-center justify-center rounded-full border leading-none font-mono font-semibold",
                diameter,
                !grad && "bg-bg-raised border-border text-fg-muted",
                grad && "border-black/20 text-transparent",
              )}
              title={p}
            />
          );
        }
        // Generic numeric cost or X/Y/Z.
        const isNumeric = /^\d+$/.test(p);
        const key = isNumeric ? "C" : (p.toUpperCase() as keyof typeof COLOR_CLASS);
        const cls = COLOR_CLASS[key] ?? COLOR_CLASS.C;
        const label = isNumeric ? p : p.toUpperCase();
        return (
          <span
            key={i}
            className={cn(
              "inline-flex items-center justify-center rounded-full border leading-none font-mono font-semibold",
              diameter,
              cls,
            )}
            title={p}
          >
            {label}
          </span>
        );
      })}
    </span>
  );
}
